import axios from '@/plugins/axios';
import { useMediaUrl, clearMediaUrlCache } from '@/composables/useMediaUrl';

const { resolveMediaUrl, invalidateMediaUrl } = useMediaUrl();

const blobUrlByKey = new Map();
const inflightByKey = new Map();
const generationByMediaId = new Map();

const ALL_VIDEO_TYPES = ['original', 'heatmap', 'fogmap'];

function makeKey(mediaId, videoType) {
    return `${mediaId}:${videoType}`;
}

function getGeneration(mediaId) {
    return generationByMediaId.get(mediaId) || 0;
}

async function fetchAndStore(mediaId, videoType) {
    const key = makeKey(mediaId, videoType);
    if (blobUrlByKey.has(key)) return blobUrlByKey.get(key);
    if (inflightByKey.has(key)) return inflightByKey.get(key);

    const generationAtStart = getGeneration(mediaId);

    const promise = axios
        .get(`/media/${mediaId}/video`, {
            params: { video_type: videoType },
            responseType: 'blob',
        })
        .then((response) => {
            const url = URL.createObjectURL(response.data);
            if (getGeneration(mediaId) !== generationAtStart) {
                URL.revokeObjectURL(url);
                inflightByKey.delete(key);
                return null;
            }
            blobUrlByKey.set(key, url);
            inflightByKey.delete(key);
            return url;
        })
        .catch((error) => {
            inflightByKey.delete(key);
            throw error;
        });

    inflightByKey.set(key, promise);
    return promise;
}

function resolveVideoType(selectedView, attentionMapStatus) {
    if (attentionMapStatus !== 'completed') return 'original';
    if (selectedView === 'Heatmap') return 'heatmap';
    if (selectedView === 'Fogmap') return 'fogmap';
    return 'original';
}

export function useMediaVideoCache() {
    function getOrFetch(mediaId, videoType) {
        if (!mediaId || !videoType) return Promise.resolve(null);
        return fetchAndStore(mediaId, videoType);
    }

    // SYN-2602 — Fonte do <video>: URL assinada do GCS primeiro (o navegador streama por
    // Range requests — primeiro frame em segundos, sem baixar 30–200 MB nem
    // segurar 3 variantes em memória). O blob via /media/{id}/video fica só
    // como fallback (backend sem o SYN-1774 ou signer fora). `streamed` diz ao
    // chamador se faz sentido prefetchar as outras variantes como blob.
    // `preferBlob` pula a URL assinada e vai direto ao blob: é a troca de
    // transporte da 2ª falha seguida do <video> (RN2/RN4 — objeto ausente no
    // bucket ou Content-Type errado nunca curam reassinando).
    async function resolveVideoSrc(mediaId, videoType, { preferBlob = false } = {}) {
        if (!mediaId || !videoType) return null;
        const signedUrl = preferBlob ? null : await resolveMediaUrl({ mediaId, asset: videoType });
        if (signedUrl) return { src: signedUrl, streamed: true };
        const blobUrl = await fetchAndStore(mediaId, videoType);
        return blobUrl ? { src: blobUrl, streamed: false } : null;
    }

    // Sequential prefetch: kicks off remaining variants ONE AT A TIME so they don't
    // saturate bandwidth or contend with the actively-playing video. Skips the
    // current variant (assumed already requested by the caller) and any variant
    // already cached. No-op when attention map isn't completed.
    async function prefetchOthersSequential(mediaId, attentionMapStatus, currentVideoType) {
        if (!mediaId || attentionMapStatus !== 'completed') return;
        const remaining = ALL_VIDEO_TYPES.filter((t) => t !== currentVideoType);
        for (const videoType of remaining) {
            if (blobUrlByKey.has(makeKey(mediaId, videoType))) continue;
            try {
                await fetchAndStore(mediaId, videoType);
            } catch (_) {
                // Best-effort prefetch; failure of one variant must not abort the rest.
            }
        }
    }

    function invalidate(mediaId) {
        if (!mediaId) return;
        // A URL assinada também: vencida, ou variante recém-gerada (heatmap pronto).
        invalidateMediaUrl(mediaId);
        // Bump generation so any in-flight fetches for this mediaId are discarded on settle.
        // We do NOT cancel the underlying axios requests — the in-flight promise still
        // resolves, but the new generation check in fetchAndStore prevents poisoning the cache.
        generationByMediaId.set(mediaId, getGeneration(mediaId) + 1);
        for (const key of Array.from(blobUrlByKey.keys())) {
            if (key.startsWith(`${mediaId}:`)) {
                URL.revokeObjectURL(blobUrlByKey.get(key));
                blobUrlByKey.delete(key);
            }
        }
        for (const key of Array.from(inflightByKey.keys())) {
            if (key.startsWith(`${mediaId}:`)) inflightByKey.delete(key);
        }
    }

    return { getOrFetch, resolveVideoSrc, prefetchOthersSequential, invalidate, resolveVideoType };
}

export function clearMediaVideoCache() {
    // Logout: as signed URLs vão junto (SYN-2602) — ver clearMediaUrlCache.
    clearMediaUrlCache();
    for (const url of blobUrlByKey.values()) URL.revokeObjectURL(url);
    blobUrlByKey.clear();
    inflightByKey.clear();
    generationByMediaId.clear();
}

export function __resetVideoCacheForTests() {
    clearMediaVideoCache();
}
